// MyCasesPage.tsx
/**
 * Leader "My Cases" — every case belonging to the signed-in leader.
 *
 * Deliberately mirrors the admin `/Counselling/Cases` page (header + refresh, search/filter card,
 * table, pagination) minus the status KPI row, which only makes sense across the whole org.
 * Rows arrive progressively via `leaderPortal.streamMyCases()`; search, filtering, sorting and
 * pagination all run client-side over the streamed set.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Container, Title, Text, Card, Badge, Button, Group, Stack, Drawer,
  ActionIcon, Tooltip, Box,
} from '@mantine/core';
import { IconSearch, IconEye, IconAdjustments, IconRefresh } from '@tabler/icons-react';
import { DateTimePicker } from '@mantine/dates';
import { Flex, Select, TextInput, CCMSDataTable, StreamProgress, ListPagination } from '@/components';
import type { TableColumn } from '@/components/ccms-components/data-display/types';
import type { SortConfig } from '@/components/ccms-components/types/common';
import { useNavigate } from 'react-router-dom';
import {
  useLeaderPortalService, useCareCaseStatusService, useLocationService, useBatchStreaming,
  LeaderCaseView, CareCaseStatus,
} from '@actschurch/ccms-sdk';
import { useSidebarCountsContext } from '@/contexts/SidebarCountsContext';
import { brandBadgeStyle } from '@/utils/color';
import { badgeStyles, noWrap } from '@/utils/tableStyles';
import LeaderPortalNotice from '../Components/LeaderPortalNotice';

const PAGE_SIZE = 10;

/** `yyyyMMddHHmmss` → `yyyy-MM-dd`. */
function formatSDKDate(value: string | number | null | undefined): string {
  const raw = String(value ?? '');
  if (raw.length < 8) return '—';
  return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
}

/** Date → the numeric form the SDK compares against, for client-side range filtering. */
function toSDKDayNumber(value: Date | null): number | null {
  if (!value) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  return Number(`${value.getFullYear()}${pad(value.getMonth() + 1)}${pad(value.getDate())}`);
}

export default function MyCasePage() {
  const navigate = useNavigate();
  const leaderPortal = useLeaderPortalService();
  const statusService = useCareCaseStatusService();
  const locationService = useLocationService();
  const { refetch } = useSidebarCountsContext();

  // ── Stream the leader's cases in 25-row batches ─────────────────────────────
  // `useBatchStreaming` swallows generator errors (it only console.errors them and clears `items`),
  // so the stream is wrapped to capture the failure and drive an inline message. Without this a 403
  // from the leader portal just looks like "you have no cases".
  const [loadError, setLoadError] = useState<unknown>(null);

  const makeStream = useCallback(() => {
    const inner = leaderPortal.streamMyCases(25);
    return (async function* () {
      try {
        for await (const batch of inner) yield batch;
      } catch (e) {
        console.error('Error streaming my cases:', e);
        setLoadError(e);
      }
    })();
  }, [leaderPortal]);

  const {
    items: allCases,
    isLoading: loading,
    progress,
    loadedCount,
    load: loadCases,
  } = useBatchStreaming<LeaderCaseView>(makeStream, [leaderPortal]);

  // Kept in a ref so the sidebar-count refresh doesn't participate in the stream effect's deps.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  const reload = useCallback(() => {
    setLoadError(null);
    return loadCases().then(() => refetchRef.current());
  }, [loadCases]);

  useEffect(() => { reload(); }, [reload]);

  // ── Table / pagination / sort ───────────────────────────────────────────────
  const [activePage, setActivePage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);

  // ── Filters ─────────────────────────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterLocationID, setFilterLocationID] = useState('');
  const [filterStartDate, setFilterStartDate] = useState<Date | null>(null);
  const [filterEndDate, setFilterEndDate] = useState<Date | null>(null);

  const isFilterActive = Boolean(filterStatus || filterLocationID || filterStartDate || filterEndDate);

  const handleClearFilters = () => {
    setFilterStatus('');
    setFilterStartDate(null);
    setFilterEndDate(null);
    setActivePage(1);
  };

  // Real status lookup rather than a hardcoded active/inactive/closed list.
  const [statuses, setStatuses] = useState<CareCaseStatus[]>([]);
  const [locations, setLocations] = useState<{ locationID: number; name: string }[]>([]);
  useEffect(() => {
    statusService
      .getAll()
      .then((s) => setStatuses(((s as any)?.data ?? s ?? []) as CareCaseStatus[]))
      .catch((e) => console.error('Error fetching case statuses:', e));
  }, [statusService]);

  useEffect(() => {
    locationService
      .getAll()
      .then((l) => setLocations((Array.isArray(l) ? l : []).filter((x) => x.isActive)))
      .catch((e) => console.error('Error fetching locations:', e));
  }, [locationService]);

  useEffect(() => { setActivePage(1); }, [searchTerm, filterStatus, filterLocationID, filterStartDate, filterEndDate]);

  // ── Client-side search + filter over the streamed set ───────────────────────
  const filteredCases = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const from = toSDKDayNumber(filterStartDate);
    const to = toSDKDayNumber(filterEndDate);

    return allCases.filter((row) => {
      if (term) {
        const matches =
          [row.name, row.needName, row.locationName, row.statusName, row.counseleeNames]
            .some((field) => (field ?? '').toLowerCase().includes(term))
          || String(row.caseID ?? '').includes(term);
        if (!matches) return false;
      }

      if (filterStatus && String(row.statusID) !== filterStatus) return false;
      // `LeaderCaseView.locationID` landed in SDK 0.0.50, so this matches on the id rather than a name.
      if (filterLocationID && String(row.locationID ?? '') !== filterLocationID) return false;

      if (from || to) {
        const opened = Number(String(row.caseDateTime ?? '').slice(0, 8));
        if (!opened) return false;
        if (from && opened < from) return false;
        if (to && opened > to) return false;
      }

      return true;
    });
  }, [allCases, searchTerm, filterStatus, filterLocationID, filterStartDate, filterEndDate]);

  // ── Client-side sort ────────────────────────────────────────────────────────
  const sortedCases = useMemo(() => {
    if (!sortConfig) return filteredCases;
    return [...filteredCases].sort((a, b) => {
      const getValue = (row: LeaderCaseView) => {
        switch (sortConfig.field) {
          case 'caseName':   return (row.name ?? '').toLowerCase();
          case 'caseOpened': return String(row.caseDateTime ?? '');
          case 'statusName': return (row.statusName ?? '').toLowerCase();
          case 'needName':   return (row.needName ?? '').toLowerCase();
          default:           return '';
        }
      };
      const aVal = getValue(a);
      const bVal = getValue(b);
      if (aVal === bVal) return 0;
      return aVal > bVal
        ? (sortConfig.direction === 'asc' ? 1 : -1)
        : (sortConfig.direction === 'asc' ? -1 : 1);
    });
  }, [filteredCases, sortConfig]);

  // ── Client-side pagination ──────────────────────────────────────────────────
  const totalCount = sortedCases.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  useEffect(() => {
    if (activePage > totalPages) setActivePage(totalPages);
  }, [activePage, totalPages]);

  const pagedCases = useMemo(() => {
    const firstIndex = (activePage - 1) * pageSize;
    return sortedCases.slice(firstIndex, firstIndex + pageSize);
  }, [sortedCases, activePage, pageSize]);

  // Both the row click and the eye button go to the leader-scoped detail route.
  const openCase = (caseID: number) => navigate(`/Counselling/MyCases/${caseID}`);

  // ── Columns ─────────────────────────────────────────────────────────────────
  const columns: TableColumn<LeaderCaseView>[] = [
    {
      key: 'caseName',
      header: 'Case',
      accessor: (row) => row.name ?? '—',
      sortable: true,
      width: 240,
      render: (_, row) => (
        <Stack gap={0}>
          <Text size="sm" fw={600}>{row.name || `Case ${row.caseID}`}</Text>
          <Text size="xs" c="dimmed">#{row.caseID}</Text>
        </Stack>
      ),
    },
    {
      key: 'needName',
      header: 'Need',
      accessor: (row) => row.needName ?? '—',
      sortable: true,
      width: 200,
      render: (value, row) =>
        row.needName ? (
          <Badge variant="filled" styles={badgeStyles} style={brandBadgeStyle(row.needColor)}>
            {value}
          </Badge>
        ) : (
          <Text size="sm" c="dimmed">—</Text>
        ),
    },
    {
      key: 'locationName',
      header: 'Location',
      accessor: (row) => row.locationName ?? '—',
      sortable: true,
      render: (value, row) =>
        row.locationName ? (
          <Badge variant="filled" styles={badgeStyles} style={brandBadgeStyle(row.locationColor)}>
            {value}
          </Badge>
        ) : (
          <Text size="sm" c="dimmed">—</Text>
        ),
    },
    {
      key: 'counselees',
      header: 'Counselees',
      accessor: (row) => row.counseleeNames ?? '—',
      sortable: false,
      width: 210,
    },
    {
      key: 'caseOpened',
      header: 'Date Opened',
      accessor: (row) => String(row.caseDateTime ?? ''),
      sortable: true,
      width: 150,
      render: (value) => <Text size="sm" style={noWrap}>{formatSDKDate(value as string)}</Text>,
    },
    {
      key: 'nextSession',
      header: 'Next Session',
      accessor: (row) => row.nextSessionDisplay ?? '—',
      sortable: false,
      width: 190,
      render: (_, row) => (
        <Text size="sm" style={noWrap}>{row.nextSessionDisplay || '—'}</Text>
      ),
    },
    {
      key: 'statusName',
      header: 'Status',
      accessor: (row) => row.statusName ?? '—',
      sortable: true,
      align: 'center',
      width: 150,
      render: (value, row) => (
        <Badge variant="filled" styles={badgeStyles} style={brandBadgeStyle(row.statusColor)}>
          {value}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      accessor: (row) => row,
      sortable: false,
      align: 'center',
      width: 70,
      render: (_, row) => (
        <Tooltip label="View">
          <ActionIcon
            variant="light"
            color="blue"
            size="sm"
            onClick={(e) => { e.stopPropagation(); openCase(row.caseID); }}
          >
            <IconEye size={16} />
          </ActionIcon>
        </Tooltip>
      ),
    },
  ];

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <Container size="xl" py="xl">
      {/* Header: title/subtitle left, Refresh far right — matches /Counselling/Cases */}
      <Group justify="space-between" align="flex-end" wrap="nowrap" mb="lg">
        <div>
          <Title order={2}>My Cases</Title>
          <Text c="dimmed">Cases assigned to you.</Text>
        </div>
        <Tooltip label="Refresh">
          <ActionIcon variant="subtle" onClick={() => reload()} loading={loading} size="lg">
            <IconRefresh size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>

      {/* A failed load is a page-level state, so it replaces the toolbar and table entirely. */}
      {loadError ? (
        <LeaderPortalNotice error={loadError} subject="your cases" onRetry={reload} />
      ) : (
      <>
      {/* Search & filter bar */}
      <Card withBorder radius="md" padding="lg" mb="md">
        <Flex gap="sm" align="center" justify="space-between" wrap="wrap">
          <Flex gap="sm" align="center">
            <TextInput
              placeholder="Search by case name, need, status, or counselee…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.currentTarget.value)}
              leftSection={<IconSearch size={16} />}
              radius="md"
              styles={{ input: { width: '320px' } }}
              size="sm"
            />
            <Button
              variant="outline"
              leftSection={<IconAdjustments size={16} />}
              onClick={() => setFiltersOpen(true)}
            >
              Filters
              {isFilterActive && <Badge ml="xs" size="xs" color="blue" circle>!</Badge>}
            </Button>
            {isFilterActive && (
              <Button variant="subtle" color="red" onClick={handleClearFilters}>
                Clear Filters
              </Button>
            )}
          </Flex>
        </Flex>
      </Card>

      {/* Filter drawer */}
      <Drawer
        opened={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        title="Filter My Cases"
        position="right"
        size="md"
        padding="md"
      >
        <Stack>
          <Select
            label="Status"
            placeholder="All Statuses"
            data={[
              { value: '', label: 'All Statuses' },
              ...statuses.map((s) => ({ value: String(s.statusID), label: s.name })),
            ]}
            value={filterStatus}
            onChange={(v) => setFilterStatus(v ?? '')}
            clearable
          />
          <Select
            label="Location"
            placeholder="All Locations"
            data={locations.map((l) => ({ value: String(l.locationID), label: l.name }))}
            value={filterLocationID}
            onChange={(v) => setFilterLocationID(v ?? '')}
            searchable
            clearable
          />
          <DateTimePicker
            label="Opened From"
            placeholder="Pick a start date"
            value={filterStartDate}
            onChange={setFilterStartDate}
            clearable
          />
          <DateTimePicker
            label="Opened To"
            placeholder="Pick an end date"
            value={filterEndDate}
            onChange={setFilterEndDate}
            clearable
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="light" onClick={() => { handleClearFilters(); setFiltersOpen(false); }}>
              Clear
            </Button>
            <Button onClick={() => setFiltersOpen(false)}>Apply</Button>
          </Group>
        </Stack>
      </Drawer>

      {/* Table */}
      <Card withBorder radius="md" padding="lg">
        <StreamProgress
          loading={loading}
          progress={progress}
          loadedCount={loadedCount}
          label="cases"
        />
        <Box style={{ overflowX: 'auto' }}>
          <CCMSDataTable
            data={pagedCases}
            columns={columns}
            loading={loading && allCases.length === 0}
            emptyMessage="You have no cases yet."
            sortable
            onSort={(config) => { setSortConfig(config); setActivePage(1); }}
            onRowClick={(row) => openCase(row.caseID)}
            striped
            highlightOnHover
          />
        </Box>
        <ListPagination
          page={activePage}
          pageSize={pageSize}
          totalItems={totalCount}
          onPageChange={(page) => setActivePage(page)}
          onPageSizeChange={(size) => { setPageSize(size); setActivePage(1); }}
          showPageSize
        />
      </Card>
      </>
      )}
    </Container>
  );
}
